import { useCallback, useMemo, useState } from 'react';
import { getTiktokHashTags, TiktokHashtag } from '@round/api';
import { ValueType } from 'react-select';
import { GenericDropdownOption } from '../../../../../App.types';
import { showNotification } from '../../../../../helpers';
import debounce from 'lodash/debounce';
import useAbortableEffect from '../../../../../Hooks/useAbortableEffect';

export type HashtagOption = GenericDropdownOption<number>;

const PAGE_SIZE = 10;

export const mapHashtagsToOption = (hashtag: TiktokHashtag): HashtagOption => ({
    value: hashtag.id,
    label: `#${hashtag.title}`,
});

export default function useHashtagSelect() {
    const [selectedHashtags, setSelectedHashtags] = useState<ValueType<HashtagOption, true>>([]);

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [isSearchLoading, setIsSearchLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);

    const [hashtags, setHashtags] = useState<TiktokHashtag[]>([]);

    const fetchData = useCallback(
        async (params: Parameters<typeof getTiktokHashTags>[0], requestInit?: RequestInit) => {
            try {
                setIsLoading(true);
                const {
                    data: { results, next },
                } = await getTiktokHashTags(params, requestInit);

                setHashtags((hashtags) => hashtags.concat(results));
                setHasNextPage(Boolean(next));
            } catch (e) {
                if (!(e instanceof Error)) {
                    showNotification('Could not fetch hashtag options', 'error');
                    return;
                }

                if (e.name === 'AbortError') {
                    return;
                }

                showNotification(e.message, 'error');
            } finally {
                setIsLoading(false);
                setIsSearchLoading(false);
            }
        },
        []
    );

    const debouncedFetch = useMemo(() => debounce(fetchData, 700), [fetchData]);

    useAbortableEffect(
        (signal) => {
            if (!search) {
                return;
            }

            debouncedFetch({ search, page, page_size: PAGE_SIZE }, { signal });
        },
        [debouncedFetch, page, search]
    );

    const reset = useCallback(() => {
        setHashtags([]);
        setPage(1);
        setHasNextPage(false);
    }, []);

    const options = useMemo(() => hashtags.map(mapHashtagsToOption), [hashtags]);

    const initSelectedOptions = useCallback((options: HashtagOption[]) => {
        setSelectedHashtags(options);
        setIsInitialized(true);
    }, []);

    return {
        selectProps: {
            options,
            value: selectedHashtags,
            onChange: (value: ValueType<HashtagOption, true>) => {
                setSelectedHashtags(value);
            },
            inputValue: search,
            onInputChange: (value: string) => {
                if (value === search) {
                    return;
                }

                reset();
                setSearch(value);

                if (value) {
                    setIsSearchLoading(false);
                }
            },
            onMenuScrollToBottom: () => {
                if (!hasNextPage) {
                    return;
                }

                setPage((prev) => prev + 1);
            },
            noOptionsMessage: () => (!search ? 'Type to search for audios' : 'No audios found'),
            isLoading: isLoading || isSearchLoading,
        },
        initSelectedOptions,
        isInitialized,
    };
}
